'use client';
import { useMemo, useState } from 'react';

import { ChatContextButton } from '@/features/chat/components';
import {
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useGetTasksQuery,
  useUpdateTaskMutation,
} from '@/features/tasks/api/tasksApi';
import {
  EditableTaskTitle,
  RepeatTaskButton,
  TaskStatusSelect,
  UpdateTaskDueDate,
  UpdateTaskStatus,
} from '@/features/tasks/components';
import { AssigneeSelect } from '@/features/workspaces/components';
import {
  Avatar,
  Badge,
  DatePicker,
  InfiniteTable,
  InlineButton,
  MainButton,
  OptionsMenu,
  PlusIcon,
  SecondaryButton,
  SmartInput,
} from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import { DeliverableTaskSubRowForm } from './DeliverableTaskSubRowForm';
import styles from './JobTasksComponents.module.scss';
import { MoveTaskDrawer } from './MoveTaskDrawer';

import type { JobKind } from '@/features/jobs/types';
import type {
  Task,
  TaskStatus,
  UpdateTaskPayload,
} from '@/features/tasks/types';
import type { Dayjs } from 'dayjs';

type DeliverableTasksTableProps = {
  jobId: string;
  deliverableId: string | null;
  retainerCycleId?: string | null;
  jobKind: JobKind;
  readOnly: boolean;
  title: string;
  availableServiceTypes?: Array<{ id: string; label: string }>;
};

type InlineCreateAllocation = {
  id: string;
  userId: string;
  serviceTypeId: string;
  levelOfEffort: number | null;
};

type InlineCreateRow = {
  id: string;
  __kind: 'inline-create';
};

type TaskRow = Task | InlineCreateRow;

const INLINE_CREATE_ROW_ID = '__deliverable-task-inline-create__';
const INLINE_CREATE_ROW: InlineCreateRow = {
  id: INLINE_CREATE_ROW_ID,
  __kind: 'inline-create',
};

function isInlineCreateRow(row: TaskRow): row is InlineCreateRow {
  return '__kind' in row;
}

function toIsoDateString(rawValue: Dayjs | null): string | null {
  if (!rawValue || !rawValue.isValid()) {
    return null;
  }

  return rawValue.startOf('day').toISOString();
}

export function DeliverableTasksTable({
  jobId,
  deliverableId,
  retainerCycleId = null,
  jobKind,
  readOnly,
  title,
  availableServiceTypes = [],
}: DeliverableTasksTableProps) {
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  const [taskToMove, setTaskToMove] = useState<Task | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [statusValue, setStatusValue] = useState<TaskStatus>('inprogress');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [dueDateValue, setDueDateValue] = useState<Dayjs | null>(null);
  const [repeatValue, setRepeatValue] = useState(false);
  const [repeatOverrides, setRepeatOverrides] = useState<
    Record<string, boolean>
  >({});
  const [allocations, setAllocations] = useState<InlineCreateAllocation[]>([
    { id: 'alloc-1', userId: '', serviceTypeId: '', levelOfEffort: null },
  ]);
  const [inlineFormError, setInlineFormError] = useState('');
  const [createTask, { isLoading: isCreatingTask }] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();
  const [deleteTask, { isLoading: isDeletingTask }] = useDeleteTaskMutation();

  const { data, isLoading, refetch } = useGetTasksQuery({
    page: 1,
    page_size: 100,
    task_type: 'job',
    job_id: jobId,
    deliverable_id: deliverableId ?? undefined,
    retainer_cycle_id: retainerCycleId ?? undefined,
    sort_field: 'created_at',
    is_descending: true,
  });

  const taskRows = useMemo<Task[]>(() => data?.data?.results ?? [], [data]);
  const rows = useMemo<TaskRow[]>(
    () => (isCreateOpen ? [...taskRows, INLINE_CREATE_ROW] : taskRows),
    [isCreateOpen, taskRows],
  );
  const restrictedServiceTypeOptions = useMemo(() => {
    const unique = new Map<string, string>();
    for (const serviceType of availableServiceTypes) {
      if (!serviceType.id || !serviceType.label) {
        continue;
      }
      if (!unique.has(serviceType.id)) {
        unique.set(serviceType.id, serviceType.label);
      }
    }
    return Array.from(unique.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [availableServiceTypes]);
  const serviceTypeSelectOptions = useMemo(
    () =>
      restrictedServiceTypeOptions.length > 0
        ? restrictedServiceTypeOptions
        : [{ value: '', label: 'No service types on this deliverable' }],
    [restrictedServiceTypeOptions],
  );

  const getPrimaryAssignee = (row: Task) => row.assignees?.[0] ?? null;
  const isRetainer = jobKind === 'retainer';
  const getTaskStatusLabel = (status: Task['status']) => {
    if (status === 'inprogress') return 'In Progress';
    if (status === 'completed') return 'Completed';
    if (status === 'archived') return 'Archived';
    return 'Backlog';
  };

  const clearInlineCreateState = () => {
    setTitleValue('');
    setStatusValue('inprogress');
    setDescriptionValue('');
    setDueDateValue(null);
    setRepeatValue(false);
    setAllocations([
      {
        id: `alloc-${Date.now()}`,
        userId: '',
        serviceTypeId: '',
        levelOfEffort: null,
      },
    ]);
    setInlineFormError('');
  };

  const openInlineCreate = () => {
    setIsCreateOpen(true);
    setExpandedRowIds((current) =>
      current.includes(INLINE_CREATE_ROW_ID)
        ? current
        : [INLINE_CREATE_ROW_ID, ...current],
    );
  };

  const closeInlineCreate = () => {
    setIsCreateOpen(false);
    setExpandedRowIds((current) =>
      current.filter((rowId) => rowId !== INLINE_CREATE_ROW_ID),
    );
    clearInlineCreateState();
  };

  const handleCreateTask = async () => {
    if (readOnly) {
      return;
    }

    if (!titleValue.trim()) {
      setInlineFormError('Task name is required.');
      return;
    }

    const validAllocations = allocations.filter(
      (allocation) =>
        allocation.userId &&
        allocation.serviceTypeId &&
        allocation.levelOfEffort !== null &&
        allocation.levelOfEffort >= 0,
    );

    if (validAllocations.length === 0) {
      setInlineFormError(
        'Add at least one allocation with assignee, service type, and LOE.',
      );
      return;
    }

    try {
      setInlineFormError('');
      await createTask({
        title: titleValue.trim(),
        description: descriptionValue.trim() || null,
        status: statusValue,
        repeat: isRetainer ? repeatValue : undefined,
        job_id: jobId,
        deliverable_id: deliverableId,
        retainer_cycle_id:
          isRetainer && retainerCycleId ? retainerCycleId : undefined,
        due_date: toIsoDateString(dueDateValue),
        task_assignee_payloads: validAllocations.map((allocation) => ({
          user_id: allocation.userId,
          service_type_id: allocation.serviceTypeId,
          level_of_effort: allocation.levelOfEffort ?? 0,
        })),
      }).unwrap();
      notify({ type: 'success', message: `Task added to ${title}.` });
      closeInlineCreate();
      void refetch();
    } catch (error) {
      setInlineFormError(getErrorMessage(error));
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'title',
        header: 'Task',
        accessor: (row: TaskRow) =>
          isInlineCreateRow(row) ? titleValue : (row.title ?? ''),
        sortable: true,
        minWidth: 210,
        cell: (row: TaskRow) => {
          if (isInlineCreateRow(row)) {
            return (
              <div className={styles.inlineTaskTitleCell}>
                <SmartInput
                  type="text"
                  name="deliverable-inline-task-title"
                  label=""
                  value={titleValue}
                  onChange={setTitleValue}
                />
                <TaskStatusSelect
                  value={statusValue}
                  label=""
                  size="small"
                  name="deliverable-inline-task-status"
                  onChange={(nextValue) => setStatusValue(nextValue)}
                />
              </div>
            );
          }

          return (
            <div className={styles.taskNameCell}>
              <EditableTaskTitle
                taskId={row.id}
                value={row.title}
                canEdit={!readOnly}
                className={styles.taskNameText}
                onUpdated={() => {
                  void refetch();
                }}
              />
              <div className={styles.taskNameMeta}>
                {row.is_draft ? (
                  <Badge label="Draft" tone="default" size="sm" />
                ) : null}
                {readOnly ? (
                  <span className={styles.taskReadonlyStatus}>
                    {getTaskStatusLabel(row.status)}
                  </span>
                ) : (
                  <UpdateTaskStatus taskId={row.id} value={row.status} />
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: 'description',
        header: 'Description',
        accessor: (row: TaskRow) =>
          isInlineCreateRow(row) ? descriptionValue : (row.description ?? ''),
        sortable: true,
        minWidth: 220,
        cell: (row: TaskRow) =>
          isInlineCreateRow(row) ? (
            <span className={styles.taskDescriptionCell}>Add description</span>
          ) : (
            <span className={styles.taskDescriptionCell}>
              {row.description?.trim() || 'Add description'}
            </span>
          ),
      },
      {
        id: 'chat',
        header: 'Chat',
        accessor: () => '',
        sortable: false,
        minWidth: 80,
        cell: (row: TaskRow) =>
          isInlineCreateRow(row) ? (
            <span className={styles.taskPlaceholderCell}>—</span>
          ) : (
            <span
              className={styles.taskChatCell}
              onClick={(event) => event.stopPropagation()}
            >
              <ChatContextButton
                target={{
                  type: 'task',
                  id: row.id,
                  label: row.title || 'Untitled task',
                  sourceJobId: jobId,
                }}
                messageCount={row.message_count}
                unreadCount={row.unread_count}
                mentionCount={row.mention_count}
              />
            </span>
          ),
      },
      {
        id: 'assignees',
        header: 'Assignee(s)',
        accessor: (row: TaskRow) => {
          if (isInlineCreateRow(row)) {
            return allocations.map((allocation) => allocation.userId).join(',');
          }
          return row.assignees
            .map((assignee) => assignee.user?.id ?? '')
            .join(',');
        },
        sortable: true,
        minWidth: 120,
        cell: (row: TaskRow) => {
          if (isInlineCreateRow(row)) {
            return (
              <span className={styles.taskPlaceholderCell}>
                {allocations.length} allocation(s)
              </span>
            );
          }

          const users = row.assignees
            .map((assignee) => assignee.user)
            .filter(Boolean)
            .slice(0, 3) as Array<{
            id: string;
            first_name?: string | null;
            last_name?: string | null;
            email?: string | null;
            avatar?: string | null;
          }>;

          if (users.length === 0) {
            return <span className={styles.taskPlaceholderCell}>—</span>;
          }

          return (
            <div className={styles.assigneeAvatars}>
              {users.map((user) => {
                const name =
                  `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
                  user.email ||
                  'User';
                return (
                  <Avatar
                    key={user.id}
                    size="small"
                    name={name}
                    src={user.avatar ?? undefined}
                  />
                );
              })}
            </div>
          );
        },
      },
      {
        id: 'service_type',
        header: 'Service Type',
        accessor: (row: TaskRow) =>
          isInlineCreateRow(row)
            ? allocations
                .map((allocation) => allocation.serviceTypeId)
                .join(',')
            : (getPrimaryAssignee(row)?.service_type?.label ?? ''),
        sortable: true,
        minWidth: 140,
        cell: (row: TaskRow) => (
          <span className={styles.taskServiceTypeCell}>
            {isInlineCreateRow(row)
              ? allocations.some((allocation) => allocation.serviceTypeId)
                ? 'Multiple'
                : '—'
              : (getPrimaryAssignee(row)?.service_type?.label ?? '—')}
          </span>
        ),
      },
      {
        id: 'due_date',
        header: 'Due Date',
        accessor: (row: TaskRow) =>
          isInlineCreateRow(row)
            ? (dueDateValue?.toISOString() ?? '')
            : (row.due_date ?? ''),
        sortable: true,
        minWidth: 120,
        cell: (row: TaskRow) =>
          isInlineCreateRow(row) ? (
            <DatePicker
              name="deliverable-inline-task-due-date"
              label=""
              value={dueDateValue}
              onChange={(nextValue) => setDueDateValue(nextValue)}
            />
          ) : readOnly ? (
            <span className={styles.taskPlaceholderCell}>
              {row.due_date || 'Select date'}
            </span>
          ) : (
            <UpdateTaskDueDate
              taskId={row.id}
              value={row.due_date}
              onUpdated={() => {
                void refetch();
              }}
            />
          ),
      },
      {
        id: 'loe',
        header: 'LOE',
        accessor: (row: TaskRow) =>
          isInlineCreateRow(row)
            ? allocations.reduce(
                (sum, allocation) => sum + (allocation.levelOfEffort ?? 0),
                0,
              )
            : row.assignees.reduce(
                (sum, assignee) => sum + (assignee.level_of_effort ?? 0),
                0,
              ),
        sortable: true,
        minWidth: 80,
        cell: (row: TaskRow) => {
          const totalHours = isInlineCreateRow(row)
            ? allocations.reduce(
                (sum, allocation) => sum + (allocation.levelOfEffort ?? 0),
                0,
              )
            : row.assignees.reduce(
                (sum, assignee) => sum + (assignee.level_of_effort ?? 0),
                0,
              );
          return <span className={styles.taskLoeCell}>{totalHours}h</span>;
        },
      },
      {
        id: 'actions',
        header: '',
        accessor: (row: TaskRow) => row.id,
        sortable: false,
        minWidth: 48,
        cell: (row: TaskRow) => {
          if (isInlineCreateRow(row)) {
            return <span className={styles.taskPlaceholderCell}>—</span>;
          }

          return readOnly ? (
            <span className={styles.taskPlaceholderCell}>—</span>
          ) : (
            <OptionsMenu
              ariaLabel="Task actions"
              actions={[
                {
                  key: 'edit',
                  label: 'Edit details',
                  onSelect: () => {
                    setExpandedRowIds((current) =>
                      current.includes(row.id) ? current : [...current, row.id],
                    );
                  },
                },
                {
                  key: 'move',
                  label: 'Move task',
                  onSelect: () => {
                    setTaskToMove(row);
                  },
                },
                {
                  key: 'delete',
                  label: 'Delete',
                  tone: 'danger',
                  loading: isDeletingTask,
                  onSelect: () => {
                    void deleteTask({ taskId: row.id })
                      .unwrap()
                      .then(() => {
                        notify({ type: 'success', message: 'Task deleted.' });
                        void refetch();
                      })
                      .catch((error) => {
                        notify({
                          type: 'error',
                          message: getErrorMessage(error),
                        });
                      });
                  },
                },
              ]}
            />
          );
        },
      },
    ],
    [
      allocations,
      deleteTask,
      descriptionValue,
      dueDateValue,
      isDeletingTask,
      jobId,
      readOnly,
      refetch,
      statusValue,
      titleValue,
    ],
  );
  const columnsWithRepeat = useMemo(() => {
    if (!isRetainer) {
      return columns;
    }
    const resolveTaskRepeat = (row: Task) =>
      repeatOverrides[row.id] ??
      Boolean((row as Task & { repeat?: boolean }).repeat);

    const repeatColumn = {
      id: 'repeat',
      header: '',
      accessor: (row: TaskRow) =>
        isInlineCreateRow(row)
          ? String(repeatValue)
          : String(resolveTaskRepeat(row)),
      sortable: false,
      minWidth: 90,
      cell: (row: TaskRow) => {
        if (isInlineCreateRow(row)) {
          return (
            <RepeatTaskButton
              isRecurring={repeatValue}
              onToggle={() => setRepeatValue((current) => !current)}
            />
          );
        }

        const currentRepeat = resolveTaskRepeat(row);
        return (
          <RepeatTaskButton
            isRecurring={currentRepeat}
            readOnly={readOnly}
            onToggle={() => {
              if (readOnly) {
                return;
              }

              const nextRepeat = !currentRepeat;
              setRepeatOverrides((current) => ({
                ...current,
                [row.id]: nextRepeat,
              }));

              void updateTask({
                taskId: row.id,
                payload: { repeat: nextRepeat },
              })
                .unwrap()
                .catch((error) => {
                  setRepeatOverrides((current) => ({
                    ...current,
                    [row.id]: currentRepeat,
                  }));
                  notify({
                    type: 'error',
                    message: getErrorMessage(error),
                  });
                });
            }}
          />
        );
      },
    };

    const actionsIndex = columns.findIndex((column) => column.id === 'actions');
    if (actionsIndex === -1) {
      return [...columns, repeatColumn];
    }

    return [
      ...columns.slice(0, actionsIndex),
      repeatColumn,
      ...columns.slice(actionsIndex),
    ];
  }, [columns, isRetainer, readOnly, repeatOverrides, repeatValue, updateTask]);

  const handleUpdateTask = async ({
    taskId,
    payload,
  }: {
    taskId: string;
    payload: UpdateTaskPayload;
  }) => {
    await updateTask({ taskId, payload }).unwrap();
    void refetch();
  };

  const inlineCreateSubForm = (
    <div className={styles.inlineTaskCreateForm}>
      <SmartInput
        type="textarea"
        name="deliverable-inline-task-description"
        label="Description"
        value={descriptionValue}
        onChange={setDescriptionValue}
      />

      <div className={styles.inlineAllocationsHeader}>
        <p className={styles.inlineAllocationsTitle}>Allocations</p>
      </div>

      <div className={styles.inlineAllocationsRows}>
        {allocations.map((allocation, index) => (
          <div key={allocation.id} className={styles.inlineAllocationRow}>
            <AssigneeSelect
              value={allocation.userId}
              onChange={(nextValue) => {
                setAllocations((current) =>
                  current.map((item) =>
                    item.id === allocation.id
                      ? { ...item, userId: nextValue }
                      : item,
                  ),
                );
              }}
              label={index === 0 ? 'Assignee' : ''}
              showPreview={false}
            />
            <SmartInput
              type="select"
              name={`allocation-service-type-${allocation.id}`}
              label={index === 0 ? 'Service Type' : ''}
              value={allocation.serviceTypeId}
              options={serviceTypeSelectOptions}
              readOnly={restrictedServiceTypeOptions.length === 0}
              onChange={(nextValue) => {
                setAllocations((current) =>
                  current.map((item) =>
                    item.id === allocation.id
                      ? { ...item, serviceTypeId: String(nextValue || '') }
                      : item,
                  ),
                );
              }}
            />
            <SmartInput
              type="number"
              name={`allocation-loe-${allocation.id}`}
              label={index === 0 ? 'LOE (hrs)' : ''}
              min={0}
              step={0.1}
              value={allocation.levelOfEffort}
              onChange={(nextValue) => {
                setAllocations((current) =>
                  current.map((item) =>
                    item.id === allocation.id
                      ? { ...item, levelOfEffort: nextValue }
                      : item,
                  ),
                );
              }}
            />
            {allocations.length > 1 ? (
              <OptionsMenu
                ariaLabel="Allocation actions"
                actions={[
                  {
                    key: 'delete',
                    label: 'Delete',
                    tone: 'danger',
                    onSelect: () => {
                      setAllocations((current) =>
                        current.filter((item) => item.id !== allocation.id),
                      );
                    },
                  },
                ]}
              />
            ) : (
              <span className={styles.taskPlaceholderCell}>-</span>
            )}
          </div>
        ))}
      </div>

      <InlineButton
        ariaLabel="Add assignee allocation"
        onClick={() =>
          setAllocations((current) => [
            ...current,
            {
              id: `alloc-${Date.now()}-${current.length}`,
              userId: '',
              serviceTypeId: '',
              levelOfEffort: null,
            },
          ])
        }
      >
        <>
          <PlusIcon size={14} />
          &nbsp;Assignee
        </>
      </InlineButton>

      {inlineFormError ? (
        <p className={styles.inlineCreateError}>{inlineFormError}</p>
      ) : null}

      <div className={styles.inlineCreateActions}>
        <SecondaryButton
          ariaLabel="Cancel task creation"
          variant="medium"
          onClick={closeInlineCreate}
        >
          <>Cancel</>
        </SecondaryButton>
        <MainButton
          ariaLabel="Save task"
          isLoading={isCreatingTask}
          onClick={handleCreateTask}
        >
          Save
        </MainButton>
      </div>
    </div>
  );

  return (
    <div className={styles.deliverableTasksTable}>
      {!readOnly && !isCreateOpen ? (
        <InlineButton onClick={openInlineCreate}>
          <PlusIcon />
          &nbsp;Add a task
        </InlineButton>
      ) : null}

      <InfiniteTable<TaskRow>
        tableId={`deliverable-tasks-table-${deliverableId ?? 'general'}`}
        columns={columnsWithRepeat}
        rows={rows}
        rowHeight={76}
        virtualizedHeight="auto"
        hasMore={false}
        isLoading={isLoading}
        skeletonRowCount={4}
        emptyMessage="No tasks yet."
        getRowId={(row) => row.id}
        getRowSortPosition={(row) =>
          isInlineCreateRow(row) ? 'first' : 'normal'
        }
        expandedRowIds={expandedRowIds}
        onExpandedRowIdsChange={(nextRowIds) => {
          if (isCreateOpen && !nextRowIds.includes(INLINE_CREATE_ROW_ID)) {
            setExpandedRowIds([...nextRowIds, INLINE_CREATE_ROW_ID]);
            return;
          }
          setExpandedRowIds(nextRowIds);
        }}
        renderRowSubComponent={(row) =>
          isInlineCreateRow(row) ? (
            inlineCreateSubForm
          ) : (
            <DeliverableTaskSubRowForm
              key={`${row.id}:${row.updated_at}`}
              task={row}
              readOnly={readOnly}
              availableServiceTypes={availableServiceTypes}
              onAllocationsChanged={() => {
                void refetch();
              }}
              onUpdateTask={handleUpdateTask}
            />
          )
        }
        isRowCollapsible={() => true}
        onLoadMore={() => {}}
      />

      <MoveTaskDrawer
        open={Boolean(taskToMove)}
        task={taskToMove}
        currentJobId={jobId}
        currentLocationLabel={title}
        onClose={() => setTaskToMove(null)}
        onMoved={() => {
          setTaskToMove(null);
          void refetch();
        }}
      />
    </div>
  );
}
